/**
 * Active warnings / criticals as compact glass rows at the top of a page (Home) or a wall tablet. Same source as the
 * drawer's Warnings (useAlerts). Tap goes to the alert's dashboard, else its history, else its room; the tablet
 * variant is read-only (Guided Access has nowhere to go). "N more" expands in place.
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { AlertItem, ModuleId } from '@home/shared';
import { useAccess, useModel } from '../../lib/hooks.js';
import { useAlerts, type Alert } from '../../lib/alerts.js';
import { alertTarget, ageShort, groupItems, stripAlerts } from '../../lib/alert-strip.js';
import { Icon } from '../../lib/icons.js';
import { openHistory } from '../../lib/ui-state.js';
import { roomHref } from '../../lib/spaces.js';
import { MODULES, isDashboard } from '../../shell/modules.js';
import './alerts.css';

export function AlertStrip({ home, max = 3, tablet = false }: { home: string; max?: number; tablet?: boolean }): ReactNode {
  const alerts = useAlerts(home);
  const model = useModel(home);
  const access = useAccess(home);
  const [open, setOpen] = useState(false);
  const { shown, more, all } = stripAlerts(alerts, open ? Infinity : max);
  if (!all.length) return null;
  const dashboard = (m: ModuleId): boolean => !!model?.modules.includes(m) && !!access?.modules.includes(m) && !!MODULES[m] && isDashboard(m);
  const icon = (a: Alert): string => (a.module && MODULES[a.module]?.icon) || a.icon;

  return (
    <section className="alert-strip" data-variant={tablet ? 'tablet' : 'page'} aria-label="Warnings" data-testid="home-alert-strip">
      {shown.map((a) => {
        const body = (
          <>
            <span className="alert-strip-icon"><Icon name={icon(a)} size={tablet ? 24 : 18} /></span>
            <span className="alert-strip-text">
              <strong>{a.title}</strong>
              {a.detail && <span>{a.detail}</span>}
            </span>
          </>
        );
        const target = tablet ? null : alertTarget(a, home, { dashboard, roomHref: (r) => roomHref(home, model, r) });
        const common = { className: 'alert-strip-row glass', 'data-tone': a.tone, 'data-testid': `strip-alert-${a.id}` };
        if (target?.kind === 'link') return <Link key={a.id} to={target.href} {...common}>{body}<Icon name="chevron" size={14} className="alert-strip-go" /></Link>;
        if (target?.kind === 'history') {
          return (
            <button key={a.id} type="button" onClick={() => openHistory(home, target.entityId)} {...common}>
              {body}<Icon name="chevron" size={14} className="alert-strip-go" />
            </button>
          );
        }
        return <div key={a.id} {...common}>{body}</div>;
      })}
      {more > 0 && (
        <button type="button" className="alert-strip-more" onClick={() => setOpen(true)} data-testid="home-alert-more">
          {more} more
        </button>
      )}
      {open && all.length > max + 1 && (
        <button type="button" className="alert-strip-more" onClick={() => setOpen(false)}>Show less</button>
      )}
    </section>
  );
}

/** Devices behind a grouped alert (devices offline), by room / integration, each with how long it's been offline. */
export function AlertItems({ items, now = Date.now() / 1000 }: { items: readonly AlertItem[]; now?: number }): ReactNode {
  const groups = groupItems(items);
  return (
    <details className="alert-items" data-testid="alert-items">
      <summary>Show {items.length} device{items.length === 1 ? '' : 's'}</summary>
      {groups.map((g) => (
        <div key={g.group} className="alert-items-group">
          <h5>{g.group}</h5>
          <ul>
            {g.items.map((it, i) => (
              <li key={`${it.name}-${i}`}>
                <span>{it.name}</span>
                <span className="alert-items-age">{ageShort(now - it.since)}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </details>
  );
}
